import React from 'react';
import PropTypes from 'prop-types';

import {Box, Button, Stack, Typography} from 'views/components';
import {translate} from 'views/module';

import moment from 'moment';

// Get a new link this long before Loom's expiry, so a play that starts just before it does not fail.
const EXPIRY_MARGIN_SECONDS = 15;

const isExpired = (link) => {
  return moment(link.expiresAt).subtract(EXPIRY_MARGIN_SECONDS, 'seconds').isSameOrBefore(moment());
};

// A plain audio player on a pre-signed S3 link `{url, expiresAt}`. The player keeps its link until it
// needs a new one: when play starts after the link expires, or when the audio does not load (S3
// refuses an expired link). `getLink` returns a promise of the new link. The player then goes back to
// the same point, and plays again if it was playing.
const RecordingPlayer = (props) => {
  const {link, getLink, label} = props;

  const [current, setCurrent] = React.useState(link);
  const [failed, setFailed] = React.useState(false);

  const audioRef = React.useRef(null);
  const isRenewing = React.useRef(false);
  const isPlaying = React.useRef(false);
  const resume = React.useRef(null);
  const retriedUrl = React.useRef(null);

  React.useEffect(() => {
    if (!current && link) setCurrent(link);
  }, [current, link]);

  const renew = async (shouldPlay) => {
    if (isRenewing.current) return;
    isRenewing.current = true;

    const audio = audioRef.current;
    resume.current = {time: audio ? audio.currentTime : 0, play: shouldPlay};

    try {
      const fresh = await getLink();
      if (!fresh) throw new Error('Loom returned no link for this recording.');

      setFailed(false);
      if (audio && fresh.url === audio.getAttribute('src')) {
        audio.load();
      } else {
        setCurrent(fresh);
      }
    } catch (error) {
      console.error(error);
      resume.current = null;
      setFailed(true);
    } finally {
      isRenewing.current = false;
    }
  };

  const onPlay = () => {
    isPlaying.current = true;
    if (isExpired(current)) {
      audioRef.current.pause();
      renew(true);
    }
  };

  const onStop = () => {
    if (!isRenewing.current) isPlaying.current = false;
  };

  // One new link for each link that fails. A new link that also fails shows the error.
  const onError = () => {
    if (retriedUrl.current === current.url) {
      setFailed(true);
      return;
    }

    retriedUrl.current = current.url;
    renew(isPlaying.current);
  };

  const onLoadedMetadata = () => {
    const audio = audioRef.current;
    const pending = resume.current;
    resume.current = null;
    if (!audio || !pending) return;

    if (pending.time) audio.currentTime = pending.time;
    if (pending.play) {
      audio.play().catch((error) => {
        console.error(error);
        isPlaying.current = false;
      });
    }
  };

  const retry = () => {
    retriedUrl.current = null;
    renew(false);
  };

  if (!current) return null;

  return (
    <Stack spacing={0.5}>
      <Box
        component='audio'
        ref={audioRef}
        controls
        preload='metadata'
        src={current.url}
        aria-label={label}
        onPlay={onPlay}
        onPause={onStop}
        onEnded={onStop}
        onError={onError}
        onLoadedMetadata={onLoadedMetadata}
        sx={{display: 'block', width: '100%', maxWidth: '420px', height: '36px'}}
      />
      {
        failed &&
        <Stack direction='row' alignItems='center' sx={{gap: 1}}>
          <Typography sx={{fontSize: '12px', color: 'error.main'}}>
            {translate('career.jobDetail.Invitations.Recording.PlayError')}
          </Typography>
          <Button size='small' variant='text' onClick={retry} sx={{textTransform: 'none', minWidth: 0, p: 0}}>
            {translate('career.jobDetail.Invitations.Recording.Retry')}
          </Button>
        </Stack>
      }
    </Stack>
  );
};
RecordingPlayer.propTypes = {
  link: PropTypes.shape({
    url: PropTypes.string.isRequired,
    expiresAt: PropTypes.string.isRequired,
  }),
  getLink: PropTypes.func.isRequired,
  label: PropTypes.string.isRequired,
};

export default React.memo(RecordingPlayer);
